import React from "react";
import { useTranslation } from "react-i18next";
import {
  Stack,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
} from "@mui/material";
import SubActionButton from "@quest_editor/components/Buttons/SubActionButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Category } from "@quest_editor/mocks";
import SearchDialog from "./SearchDialog";

const CategoryField = ({
  categoryRows,
  setCategoryRows,
  emptyLabel,
}: {
  categoryRows: Category[];
  setCategoryRows: React.Dispatch<React.SetStateAction<Category[]>>;
  emptyLabel: string;
}) => {
  const { t } = useTranslation("questEditor");
  const [name, setName] = React.useState<string>("");
  const [open, setOpen] = React.useState(false);

  const handleRemoveCategory = (id: string) => {
    setCategoryRows((prev: Category[]) => prev.filter((row) => row.id !== id));
  };

  const handleAddSearchDialog = (selected: Category[]) => {
    setCategoryRows((prev: Category[]) => {
      const newItems = selected.filter((selectedItem) => {
        const isAlreadyAdded = prev.some(
          (existingItem) =>
            existingItem.major === selectedItem.major &&
            existingItem.middle === selectedItem.middle &&
            existingItem.small === selectedItem.small &&
            existingItem.detail === selectedItem.detail,
        );
        return !isAlreadyAdded;
      });
      return [...prev, ...newItems];
    });
    setName("");
  };

  const handleOpenSearch = () => {
    setOpen(true);
  };

  return (
    <Accordion
      sx={{
        ".MuiAccordionSummary-root .MuiSvgIcon-root": {
          transform: "rotate(0deg)",
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {categoryRows.length > 0 ? (
          <Typography color="primary">
            {categoryRows.map((c) => c.detail).join(", ")}
          </Typography>
        ) : (
          <Typography color="primary">{emptyLabel}</Typography>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="column" divider={<Divider />} spacing={1}>
          {categoryRows.map((row) => (
            <Stack key={row.id} direction="row" alignItems="center" spacing={4}>
              <Typography sx={{ color: "text.secondary", flexGrow: 1 }}>
                {t("common_field.category.label.major", {
                  name: row.major,
                })}
              </Typography>
              <ChevronRightIcon />
              <Typography sx={{ color: "text.secondary", flexGrow: 1 }}>
                {t("common_field.category.label.middle", {
                  name: row.middle,
                })}
              </Typography>
              <ChevronRightIcon />
              <Typography sx={{ color: "text.secondary", flexGrow: 1 }}>
                {t("common_field.category.label.small", {
                  name: row.small,
                })}
              </Typography>
              <ChevronRightIcon />
              <Typography sx={{ color: "text.secondary", flexGrow: 1 }}>
                {t("common_field.category.label.detail", {
                  name: row.detail,
                })}
              </Typography>
              <IconButton onClick={() => handleRemoveCategory(row.id)}>
                <ClearIcon />
              </IconButton>
            </Stack>
          ))}
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              size="small"
              placeholder={t("common_field.category.placeholder")}
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
              sx={{ flexGrow: 1 }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") handleOpenSearch();
              }}
            />
            <SubActionButton
              onClick={handleOpenSearch}
              sx={{ whiteSpace: "nowrap" }}
            >
              {t("common_field.category.button")}
            </SubActionButton>

            <SearchDialog
              open={open}
              onClose={() => setOpen(false)}
              onAdd={handleAddSearchDialog}
              inputValue={name}
              existingRows={categoryRows}
            />
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default CategoryField;
